import React, { useEffect, useMemo, useRef } from 'react';
import { connect } from "react-redux";
import {
  BACKSPLASH_LABELS,
  EDGE_LABELS,
  SIDEBAR_HIDDEN_BUTTONS,
  SIDEBAR_NAVIGATION_BUTTONS,
  SIDEBAR_SHAPE_BUTTONS,
  SIDEBAR_TYPE_AREA_NAVIGATION_BUTTONS,
  SQFT_FEATURES
} from "../../../../constants";
import { FormattedMessage, injectIntl } from "react-intl";
import $ from "jquery";
import { addChangeValuePopup, setSQFTActiveButton, setSQFTMenuOpened } from "../../../../../../actions/sqftMenuFunctions";
import EventManager from "../../../canvas/store/sqftEventManager";
import workspace from "../../../canvas/core/workspace";
import config from "../../../canvas/store/sqftConfig";
import FreeDraw from "../../../canvas/core/controls/freeDraw";
import { getIsSFTAreaGroup } from "../../../../helpers/sqftNavigationHelper";
import WaterfallController from "../../../canvas/core/controls/WaterfallController";
import { handleFreeFormClick, handleMiteredClick, handleUnderlaymentClick } from "../../NavigationHelper";
import { getCommonVisualizerType } from "../../../../../../config";
import useFetchFeatures from "../../../../hocs/useFetchFeatures";
import LoadingSpinner from "../../../../../ui/loadingSpinner";
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { hideSqftTooltip, showSqftTooltip } from "../../../../../../actions/sqftTooltipFunctions";

export const resetEvents = function () {
  EventManager.stealCursor(true);
  EventManager.resetCursor(self);
  workspace.setAllowEdges(false);
  workspace.setAllowBumpoutEdges(false);
};

/**
 * buttonKey -> dependency keys in featuresData
 */
const COUNTERTOP_MENU_BUTTON_DEPS = {
  [SIDEBAR_NAVIGATION_BUTTONS.end_cap]: [SQFT_FEATURES.end_cap],
  [SIDEBAR_NAVIGATION_BUTTONS.edge]: [...Object.keys(EDGE_LABELS)],
  [SIDEBAR_NAVIGATION_BUTTONS.backsplash]: [...Object.keys(BACKSPLASH_LABELS)],
};

/**
 * Fetch only what is needed for this menu.
 */
const COUNTERTOP_MENU_FETCH_KEYS = Array.from(
  new Set(Object.values(COUNTERTOP_MENU_BUTTON_DEPS).flat())
);

/**
 * Generic availability:
 * - Remove if no allNavItems found across deps
 * - Disable if allNavItems exist but no rows across deps
 */
const getButtonAvailability = ({ featuresData, deps }) => {
  const depKeys = Array.isArray(deps) ? deps : [];
  const items = depKeys
    .map(key => featuresData?.[key])
    .filter(Boolean);

  const hasAnyAllNavItems = items.some(item => item?.allNavItems?.length > 0);
  if (!hasAnyAllNavItems) {
    return { shouldRemove: true, isDisabled: false, disableReason: "not_available" };
  }

  const hasAnyRows = items.some(item => (item?.rows?.length ?? 0) > 0);
  if (!hasAnyRows) {
    const wasFilteredByAR3D = items.some(item => !!item?.wasFilteredByAR3D);
    return {
      shouldRemove: false,
      isDisabled: true,
      disableReason: wasFilteredByAR3D ? "missing_3d_models" : "not_available",
    };
  }

  return { shouldRemove: false, isDisabled: false, disableReason: "not_available" };
};

const SqftToolMenuCountertop = (props) => {
  const {
    activeButton,
    menuOpened,
    setSQFTActiveButton,
    setSQFTMenuOpened,
    intl,
    showSqftTooltip,
    hideSqftTooltip
  } = props;

  // const wizContext = useContext(MaterialsWizardContext);
  // const { wizardSteps } = wizContext;

  const isSFTAreaGroup = getIsSFTAreaGroup();
  let buttons = isSFTAreaGroup ? SIDEBAR_TYPE_AREA_NAVIGATION_BUTTONS : SIDEBAR_NAVIGATION_BUTTONS;

  const expanded = activeButton
    && menuOpened !== SIDEBAR_NAVIGATION_BUTTONS.bumpout
    && menuOpened !== SIDEBAR_HIDDEN_BUTTONS.toolWaterfall
    && menuOpened !== SIDEBAR_HIDDEN_BUTTONS.toolUnderlayment
    && menuOpened !== SIDEBAR_NAVIGATION_BUTTONS.mitered
    && (menuOpened === SIDEBAR_NAVIGATION_BUTTONS.move || Object.keys(buttons).some(buttonKey => menuOpened === buttonKey));

  const partIds = useMemo(() => {
    if (!expanded) return [];
    return COUNTERTOP_MENU_FETCH_KEYS;
  }, [expanded]);

  const { featuresData, isLoading } = useFetchFeatures(partIds);

  // Stores per-button state
  const buttonStates = {};
  const buttonDisableReasons = {};

  if (!isLoading && featuresData) {
    buttons = Object.keys(buttons).reduce((newButtons, buttonKey) => {
      let shouldRemove = false;
      let isDisabled = false;
      let disableReason = "not_available";

      // toolWaterfall button in fireplace mode
      if (buttonKey === SIDEBAR_HIDDEN_BUTTONS.toolWaterfall && getCommonVisualizerType() === "fireplace") {
        shouldRemove = true;
      }
      //
      // // backsplash gating via wizard steps (business rule)
      // if (
      //   buttonKey === SIDEBAR_NAVIGATION_BUTTONS.backsplash
      //   && (wizardSteps?.length && !wizardStepsHasGroupName(wizardSteps, "Backsplash"))
      // ) {
      //   isDisabled = true;
      //   disableReason = "not_available";
      // }

      // deps-based availability (edge, end_cap)
      const deps = COUNTERTOP_MENU_BUTTON_DEPS[buttonKey];
      if (!isDisabled && deps?.length) {
        const availability = getButtonAvailability({ featuresData, deps });
        shouldRemove = availability.shouldRemove;
        isDisabled = availability.isDisabled;
        disableReason = availability.disableReason;
      }

      if (shouldRemove) {
        return newButtons;
      }

      buttonStates[buttonKey] = isDisabled;
      buttonDisableReasons[buttonKey] = disableReason;
      newButtons[buttonKey] = buttons[buttonKey];
      return newButtons;
    }, {});
  }

  // Ref to track mount state
  const isMountedRef = useRef(true);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleButtonClick = (buttonKey) => {
    // Prevent clicking on disabled buttons
    if (buttonStates[buttonKey]) return;

    setSQFTMenuOpened(buttonKey);
    setSQFTActiveButton(buttonKey);
    config.workspace.menuItemActive = buttonKey;
    resetEvents();

    switch (buttonKey) {
      case SIDEBAR_NAVIGATION_BUTTONS.move:
        setSQFTMenuOpened('');
        break;
      case SIDEBAR_SHAPE_BUTTONS.freeDraw:
      case SIDEBAR_TYPE_AREA_NAVIGATION_BUTTONS.freeDraw:
        FreeDraw.init();
        break;
      case SIDEBAR_SHAPE_BUTTONS.shapeFreeForm:
        handleFreeFormClick();
        break;
      case SIDEBAR_NAVIGATION_BUTTONS.resize:
        setSQFTMenuOpened('');
        setSQFTActiveButton(SIDEBAR_NAVIGATION_BUTTONS.resize);
        handleResizeClick();
        break;
      case SIDEBAR_NAVIGATION_BUTTONS.rotate:
        setSQFTMenuOpened('');
        setSQFTActiveButton(SIDEBAR_NAVIGATION_BUTTONS.rotate);
        handleRotateClick();
        break;
      case SIDEBAR_NAVIGATION_BUTTONS.angle:
        setSQFTMenuOpened('');
        handleAngleClick();
        break;
      case SIDEBAR_NAVIGATION_BUTTONS.text:
        setSQFTMenuOpened('');
        handleTextClick();
        break;
      case SIDEBAR_NAVIGATION_BUTTONS.label:
        setSQFTMenuOpened('');
        break;
      case SIDEBAR_NAVIGATION_BUTTONS.mitered:
        setSQFTMenuOpened(SIDEBAR_NAVIGATION_BUTTONS.mitered);
        handleMiteredClick();
        break;
      case SIDEBAR_NAVIGATION_BUTTONS.duplicate:
        setSQFTMenuOpened('');
        handleDuplicateClick();
        break;
      case SIDEBAR_HIDDEN_BUTTONS.toolWaterfall:
        handleWaterfallClick();
        break;
      case SIDEBAR_HIDDEN_BUTTONS.toolUnderlayment:
        setSQFTMenuOpened(SIDEBAR_HIDDEN_BUTTONS.toolUnderlayment);
        handleUnderlaymentClick();
        break;
      case 'reset':
        handleResetClick();
        break;
    }

    $('.lightbox-option-selected').removeClass('lightbox-option-selected');
  };

  const handleMouseEnter = (event, buttonKey) => {
    if (!buttonStates[buttonKey]) return;

    const targetRect = event.currentTarget.getBoundingClientRect();
    const featureName = intl.formatMessage({ id: "sqft.navigation.submenu." + buttonKey });
    const reason = buttonDisableReasons[buttonKey];

    let title;
    let description;

    if (reason === "missing_3d_models") {
      title = intl.formatMessage({ id: "sqft.navigation.missing_3d_models_title" });
      description = `${featureName} ${intl.formatMessage({ id: "sqft.navigation.missing_3d_models_description" })}`;
    } else {
      title = `${featureName} ${intl.formatMessage({ id: "sqft.navigation.not_available_title" })}`;
      description = `${featureName} ${intl.formatMessage({ id: "sqft.navigation.not_available_description" })}`;
    }

    showSqftTooltip({
      title,
      description,
      icon: <AutoFixHighIcon />,
      position: 'right',
      targetRect
    });
  };

  const handleMouseLeave = () => {
    hideSqftTooltip();
  };

  const handleResizeClick = function () {
    EventManager.stealCursor(true);
    EventManager.resetCursor(self);
    workspace.setAllowEdges(true);
    workspace.setAllowLineHighlight(true);
    setTimeout(function () {
      workspace.setAllowLineHighlight(false);
    }, 1000);
  };

  const handleRotateClick = function () {
    EventManager.stealCursor(true);
    config.workspace.menuItemActive = "rotate-left";
  };

  const handleDuplicateClick = function () {
    EventManager.stealCursor(true);
    EventManager.setCursor("duplicate", this, true);
    config.workspace.menuItemActive = "duplicate";
  };

  const handleAngleClick = function () {
    EventManager.stealCursor(true);
    EventManager.resetCursor(self);
    config.highlight.rotateAngle = true;
    workspace.getShapes().forEach((shape) => {
      if (Array.isArray(shape.rotateAngleParts) && shape.rotateAngleParts.length) {
        // TODO: fix bumpout rotation and remove this
        if (shape.isWithBumpout()) return;
        shape.activateRotateAngle();
      }
    });
  };

  const handleTextClick = function () {
    // Empty function - placeholder for future implementation
  };

  const handleResetClick = function () {
    EventManager.stealCursor(true);
    EventManager.resetCursor(self);
  };

  const handleWaterfallClick = function () {
    workspace.setAllowBumpoutEdges(true);
    workspace.setAllowLineHighlight(true);
    WaterfallController.addWaterfall_onClick();
    setTimeout(function () {
      workspace.setAllowLineHighlight(false);
    }, 1000);
  };

  return (
    <nav className={"navigation side" + (expanded ? " expanded" : "")} data-side="navigation">
      <div className="navigation-list">
        {isLoading
          ? <LoadingSpinner />
          : !expanded ? null : Object.keys(buttons).map((buttonKey) => {
            const isDisabled = buttonStates[buttonKey];

            return (
              <button
                key={buttonKey}
                className={
                  "animated fadeInUp button button--" + buttonKey +
                  (isDisabled ? " button--disabled" : "")
                }
                onClick={() => handleButtonClick(buttonKey)}
                onMouseEnter={(e) => handleMouseEnter(e, buttonKey)}
                onMouseLeave={handleMouseLeave}
              >
                <span className="button-text">
                  <FormattedMessage id={"sqft.navigation.submenu." + buttonKey} />
                </span>
              </button>
            );
          })}
      </div>
    </nav>
  );
};

const mapStateToProps = (store) => ({
  menuOpened: store.sqftMenuState.menuOpened,
  activeButton: store.sqftMenuState.activeButton
});

const mapDispatchToProps = {
  setSQFTMenuOpened,
  setSQFTActiveButton,
  addChangeValuePopup,
  showSqftTooltip,
  hideSqftTooltip,
};

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(SqftToolMenuCountertop)
);
